import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ClauseLibraryPanel = () => {
  const [selectedCategory, setSelectedCategory] = useState('probation');
  const [searchTerm, setSearchTerm] = useState('');

  const clauseCategories = [
    { id: 'probation', label: 'Probation Periods', icon: 'Clock' },
    { id: 'termination', label: 'Termination Conditions', icon: 'XCircle' },
    { id: 'nasfund', label: 'NASFUND Obligations', icon: 'DollarSign' },
    { id: 'overtime', label: 'Overtime Provisions', icon: 'Clock' },
    { id: 'leave', label: 'Leave Entitlements', icon: 'Calendar' },
    { id: 'confidentiality', label: 'Confidentiality', icon: 'Lock' }
  ];

  const clauseLibrary = {
    probation: [
      {
        id: 'prob-1',
        title: 'Standard Probation Period (3 months)',
        text: 'The Employee shall be on probation for a period of three (3) months from the commencement date. During this period, either party may terminate the employment with one (1) week notice or payment in lieu thereof, in accordance with Section 35 of the Employment Act 1978.',
        version: '2.1',
        approved: true,
        lastUpdated: '2024-01-15'
      },
      {
        id: 'prob-2',
        title: 'Extended Probation Period (6 months)',
        text: 'The Employee shall be on probation for a period of six (6) months from the commencement date. Performance reviews will be conducted at 3 months and 5 months. Either party may terminate the employment during probation with two (2) weeks notice.',
        version: '2.0',
        approved: true,
        lastUpdated: '2024-01-10'
      }
    ],
    termination: [
      {
        id: 'term-1',
        title: 'Standard Notice Period',
        text: 'Either party may terminate this contract by giving not less than four (4) weeks written notice, or payment in lieu thereof. This is in accordance with Section 36 of the Employment Act 1978.',
        version: '3.0',
        approved: true,
        lastUpdated: '2024-02-01'
      },
      {
        id: 'term-2',
        title: 'Summary Dismissal Clause',
        text: 'The Employer may terminate employment without notice in cases of serious misconduct, including but not limited to: theft, fraud, assault, willful damage to property, or gross insubordination, as defined in Section 37 of the Employment Act 1978.',
        version: '2.5',
        approved: true,
        lastUpdated: '2024-01-20'
      },
      {
        id: 'term-3',
        title: 'Redundancy Provisions',
        text: 'In the event of redundancy, the Employee shall be entitled to redundancy pay calculated at two (2) weeks pay for each completed year of service, subject to a minimum of four (4) weeks pay, in accordance with PNG employment standards.',
        version: '2.2',
        approved: true,
        lastUpdated: '2024-01-18'
      }
    ],
    nasfund: [
      {
        id: 'nas-1',
        title: 'NASFUND Superannuation Contribution',
        text: 'The Employer shall contribute 8.4% of the Employee\'s gross salary to the National Superannuation Fund (NASFUND) in accordance with the Superannuation (General Provisions) Act 2000. The Employee may make voluntary additional contributions.',
        version: '1.8',
        approved: true,
        lastUpdated: '2024-01-25'
      },
      {
        id: 'nas-2',
        title: 'Superannuation Registration',
        text: 'The Employer shall register the Employee with NASFUND within fourteen (14) days of commencement and provide the Employee with their member number and contribution statements quarterly.',
        version: '1.5',
        approved: true,
        lastUpdated: '2024-01-12'
      }
    ],
    overtime: [
      {
        id: 'over-1',
        title: 'Standard Overtime Rate (1.5x)',
        text: 'All hours worked in excess of the standard working hours shall be paid at one and a half times (1.5x) the Employee\'s ordinary hourly rate, in accordance with Section 42 of the Employment Act 1978.',
        version: '2.3',
        approved: true,
        lastUpdated: '2024-02-05'
      },
      {
        id: 'over-2',
        title: 'Public Holiday Overtime (2.5x)',
        text: 'Work performed on public holidays shall be paid at two and a half times (2.5x) the Employee\'s ordinary hourly rate, unless the Employee is granted a day off in lieu with the Employer\'s agreement.',
        version: '2.1',
        approved: true,
        lastUpdated: '2024-01-30'
      }
    ],
    leave: [
      {
        id: 'leave-1',
        title: 'Annual Leave Entitlement',
        text: 'The Employee is entitled to four (4) weeks paid annual leave per year of service, accruing progressively. Leave may be taken by mutual agreement and must be taken within eighteen (18) months of accrual.',
        version: '2.0',
        approved: true,
        lastUpdated: '2024-01-22'
      },
      {
        id: 'leave-2',
        title: 'Sick Leave Provisions',
        text: 'The Employee is entitled to paid sick leave of up to two (2) weeks per year. Medical certificates are required for absences exceeding two (2) consecutive days. Unused sick leave does not accumulate.',
        version: '1.9',
        approved: true,
        lastUpdated: '2024-01-15'
      },
      {
        id: 'leave-3',
        title: 'Maternity Leave',
        text: 'Female employees are entitled to six (6) weeks paid maternity leave, in accordance with the Employment Act 1978. Additional unpaid leave may be granted by mutual agreement.',
        version: '1.7',
        approved: true,
        lastUpdated: '2024-01-10'
      }
    ],
    confidentiality: [
      {
        id: 'conf-1',
        title: 'Confidentiality and Non-Disclosure',
        text: 'The Employee agrees to maintain strict confidentiality regarding all business information, trade secrets, client data, and proprietary information of the Employer, both during and after employment.',
        version: '2.4',
        approved: true,
        lastUpdated: '2024-02-01'
      }
    ]
  };

  const selectedClauses = clauseLibrary?.[selectedCategory] || [];
  const filteredClauses = selectedClauses?.filter(clause =>
    clause?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    clause?.text?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">PNG Employment Contract Clause Library</h3>
          <p className="text-sm text-gray-600 mt-1">Pre-approved legal language aligned with Employment Act 1978</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Icon name="Search" className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search clauses..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2">
        {clauseCategories?.map((category) => (
          <button
            key={category?.id}
            onClick={() => setSelectedCategory(category?.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              selectedCategory === category?.id
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Icon name={category?.icon} className="h-4 w-4" />
            {category?.label}
          </button>
        ))}
      </div>

      {/* Clause Cards */}
      <div className="space-y-4">
        {filteredClauses?.length > 0 ? (
          filteredClauses?.map((clause) => (
            <div key={clause?.id} className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold text-gray-900">{clause?.title}</h4>
                    {clause?.approved && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                        <Icon name="CheckCircle" className="h-3 w-3" />
                        Approved
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">{clause?.text}</p>
                </div>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span>Version {clause?.version}</span>
                  <span>Updated: {new Date(clause?.lastUpdated)?.toLocaleDateString()}</span>
                </div>
                <Button
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                >
                  <Icon name="Copy" className="h-4 w-4" />
                  Copy Clause
                </Button>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12 text-gray-500">
            <Icon name="Search" className="h-12 w-12 mx-auto mb-3 text-gray-400" />
            <p>No clauses found matching your search</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClauseLibraryPanel;